import { z } from 'zod'

// Onboarding checklist state — stored on Organization.onboardingState (JSON).
// Records dismissal of the Today onboarding widget plus per-step skip and
// completion-acknowledgement events with who/when attribution. Org-level fact,
// shared across all admins/owners of the organization.
//
// v2 adds the `flow` object: resumable progress through the full-page Guided
// Setup experience (/getting-started). The widget and the flow are two views
// of this one state, so "continue later" works across surfaces and devices.

export const OnboardingStateEventSchema = z.object({
  /** ISO-8601 timestamp */
  at: z.string(),
  /** User ID of the actor */
  by: z.string(),
})

export type OnboardingStateEvent = z.infer<typeof OnboardingStateEventSchema>

/**
 * Rollup of a completed import run for one module, denormalized here so the
 * widget and finale screens can show "34 contacts imported" without querying
 * import_jobs. Written by the Guided Setup flow when an import job completes.
 */
export const OnboardingImportStatsSchema = z.object({
  created: z.number(),
  updated: z.number(),
  skipped: z.number(),
  errors: z.number().optional(),
  importJobId: z.string().optional(),
  /** ISO-8601 timestamp of the import completion. */
  at: z.string().optional(),
})

export type OnboardingImportStats = z.infer<typeof OnboardingImportStatsSchema>

export const OnboardingFlowStateSchema = z.object({
  /**
   * Subscription plan key ('free' | 'pro' | 'business') captured when the flow
   * first started. Display-only — live gating always re-derives from the
   * current subscription so upgrades/downgrades are reflected.
   */
  planKeyAtStart: z.string().nullable().optional(),
  /** ISO-8601 — first time the guided flow was opened for this org. */
  startedAt: z.string().optional(),
  /** ISO-8601 — last interaction with the guided flow. */
  lastActiveAt: z.string().optional(),
  /**
   * Resume pointer: the flow step the user should land on next. Null once the
   * flow has been finished (or never started).
   */
  currentStepId: z.string().nullable().optional(),
  /** Flow steps explicitly completed or advanced past, with attribution. */
  completedSteps: z.record(z.string(), OnboardingStateEventSchema).optional(),
  /** Per-module import rollups, keyed by ModuleType (e.g. 'CONTACT'). */
  importStats: z.record(z.string(), OnboardingImportStatsSchema).optional(),
  /** ISO-8601 — set when the user reaches the finale. Ends auto-resume. */
  completedAt: z.string().nullable().optional(),
})

export type OnboardingFlowState = z.infer<typeof OnboardingFlowStateSchema>

export const OnboardingStateSchema = z.object({
  /** Whole-widget dismissal. Null/absent means visible. */
  dismissed: OnboardingStateEventSchema.nullable().optional(),
  /** Steps explicitly skipped by an admin. Counted as done for progress. */
  skipped: z.record(z.string(), OnboardingStateEventSchema).optional(),
  /**
   * First time the widget observed a step as complete (and by whom). An
   * attribution stamp only — it does not gate progress (real completion is
   * still derived from underlying record counts).
   */
  acknowledged: z.record(z.string(), OnboardingStateEventSchema).optional(),
  /** Guided Setup flow progress (v2). Absent for orgs that predate the flow. */
  flow: OnboardingFlowStateSchema.optional(),
})

export type OnboardingState = z.infer<typeof OnboardingStateSchema>

export function isOnboardingState(value: unknown): value is OnboardingState {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('dismissed' in value ||
      'skipped' in value ||
      'acknowledged' in value ||
      'flow' in value)
  )
}

/**
 * Server-computed completion signals for onboarding, aggregated in one request
 * (GET /organization/:id/onboarding-summary) instead of N per-module count
 * queries from the client.
 */
export interface OnboardingSummary {
  memberCount: number
  entityCount: number
  projectCount: number
  taskCount: number
  contactCount: number
  supplierCount: number
  dealCount: number
  rateCardCount: number
  /** Org-level + current-user integration connections combined. */
  connectionCount: number
  /**
   * Completed import jobs rolled up per module type: total records created /
   * updated / skipped across all COMPLETED jobs for that module.
   */
  importTotals: Record<
    string,
    { created: number; updated: number; skipped: number; jobs: number }
  >
}

/** The comparison shape used by every onboarding writer, including legacy {}. */
export function normalizeOnboardingState(raw: unknown): OnboardingState {
  const parsed = OnboardingStateSchema.safeParse(raw)
  const state = parsed.success ? parsed.data : {}
  return {
    dismissed: state.dismissed ?? null,
    skipped: state.skipped ?? {},
    acknowledged: state.acknowledged ?? {},
    ...(state.flow ? { flow: state.flow } : {}),
  }
}
